import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { TrendingUp, Zap, Repeat, Award } from "lucide-react";

const comparisons = [
  {
    channel: "Anuncios de pago (Ads)",
    cost: "€€€€",
    lifetime: "0 días tras cortar inversión",
    trust: "Baja (interrupción)",
    output: "1 anuncio → 1 anuncio",
    highlight: false,
  },
  {
    channel: "Blog / SEO",
    cost: "€€",
    lifetime: "Sí (evergreen)",
    trust: "Media",
    output: "1 artículo → 1 pieza",
    highlight: false,
  },
  {
    channel: "Vídeopodcast Galvani",
    cost: "€€",
    lifetime: "Sí (evergreen)",
    trust: "Alta (autoridad + cercanía)",
    output: "1 grabación → 10+ piezas",
    highlight: true,
  },
];

const highlights = [
  {
    icon: Repeat,
    metric: "10+",
    label: "Piezas de contenido por episodio",
    description: "YouTube, Spotify, Reels, LinkedIn, Newsletter, TikTok…",
    gradient: "from-blue-500 to-cyan-500",
  },
  {
    icon: TrendingUp,
    metric: "82%",
    label: "De decisores B2B consumen podcasts",
    description: "Estás llegando a quien realmente compra tu producto.",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    icon: Zap,
    metric: "4.5x",
    label: "Más engagement que un blog",
    description: "El vídeo genera conexión emocional que el texto no consigue.",
    gradient: "from-pink-500 to-red-500",
  },
  {
    icon: Award,
    metric: "6 meses",
    label: "Para posicionarte como referente",
    description: "Con constancia y estrategia, cambias tu percepción en el mercado.",
    gradient: "from-cyan-500 to-blue-500",
  },
];

export default function ROISection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="section-spacing bg-white" data-testid="roi-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-purple font-body font-semibold mb-4">
            El ROI del videopodcast
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            Un canal caro no es el que <span className="gradient-text">cuesta más</span>, es el que <span className="gradient-text">rinde menos</span>
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            Compara honestamente el vídeopodcast con las principales inversiones de marketing.
            Verás por qué tantas empresas están cambiando su mix.
          </p>
        </motion.div>

        {/* Highlights grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {highlights.map((h, i) => (
            <motion.div
              key={h.label}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="p-6 rounded-2xl bg-white border border-slate-200 hover:shadow-lg transition-all"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${h.gradient} flex items-center justify-center mb-4`}>
                <h.icon className="w-6 h-6 text-white" />
              </div>
              <div className={`font-heading font-extrabold text-4xl bg-gradient-to-r ${h.gradient} bg-clip-text text-transparent mb-2`}>
                {h.metric}
              </div>
              <h3 className="font-heading font-semibold text-galvani-text mb-2">{h.label}</h3>
              <p className="font-body text-sm text-galvani-textLight leading-relaxed">{h.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="overflow-x-auto rounded-3xl border border-slate-200 shadow-xl"
        >
          <table className="w-full min-w-[720px] bg-white">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="p-5 text-left font-heading font-semibold text-galvani-text text-sm">Canal</th>
                <th className="p-5 text-center font-heading font-semibold text-galvani-text text-sm">Inversión</th>
                <th className="p-5 text-center font-heading font-semibold text-galvani-text text-sm">Contenido perdurable</th>
                <th className="p-5 text-center font-heading font-semibold text-galvani-text text-sm">Confianza generada</th>
                <th className="p-5 text-center font-heading font-semibold text-galvani-text text-sm">Contenido derivado</th>
              </tr>
            </thead>
            <tbody>
              {comparisons.map((c, idx) => (
                <tr
                  key={c.channel}
                  className={`${idx < comparisons.length - 1 ? "border-b border-slate-100" : ""} ${
                    c.highlight ? "bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50" : ""
                  }`}
                >
                  <td className={`p-5 font-heading font-semibold text-sm ${c.highlight ? "text-galvani-purple" : "text-galvani-text"}`}>
                    {c.channel}
                    {c.highlight && (
                      <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs bg-galvani-purple text-white font-body">
                        Recomendado
                      </span>
                    )}
                  </td>
                  <td className="p-5 text-center font-body text-sm text-galvani-textLight">{c.cost}</td>
                  <td className="p-5 text-center font-body text-sm text-galvani-textLight">{c.lifetime}</td>
                  <td className="p-5 text-center font-body text-sm text-galvani-textLight">{c.trust}</td>
                  <td className="p-5 text-center font-body text-sm text-galvani-textLight">{c.output}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
}
